import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/api";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";

const AgencyEmployeesPage = () => {
  const { token, user } = useAuth();
  const { t } = useTranslation();

  const isAgencyOwner =
    user?.role === "AGENCY" && (!user?.agencyRole || user?.agencyRole === "OWNER");

  const [employees, setEmployees] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [newEmpEmail, setNewEmpEmail] = useState("");
  const [newEmpRole, setNewEmpRole] = useState("EMPLOYEE");
  const [inviting, setInviting] = useState(false);

  const [accessLogs, setAccessLogs] = useState([]);
  const [accessLogsLoading, setAccessLogsLoading] = useState(false);

  useEffect(() => {
    if (!token || !isAgencyOwner) return;
    (async () => {
      try {
        setEmpLoading(true);
        const list = await api.agencyListUsers(token);
        setEmployees(Array.isArray(list) ? list : []);
      } catch (_) {
        setEmployees([]);
      } finally {
        setEmpLoading(false);
      }
    })();
  }, [token, isAgencyOwner]);

  useEffect(() => {
    if (!token || !isAgencyOwner) return;
    (async () => {
      try {
        setAccessLogsLoading(true);
        const resp = await api.agencyAccessLogs(token, { days: 365, limit: 1000 });
        setAccessLogs(Array.isArray(resp?.items) ? resp.items : []);
      } catch (_) {
        setAccessLogs([]);
      } finally {
        setAccessLogsLoading(false);
      }
    })();
  }, [token, isAgencyOwner]);

  const lastSwitchByAgencyUserId = useMemo(() => {
    const map = new Map();
    for (const entry of accessLogs) {
      const uid = entry?.user?.id;
      if (!uid) continue;
      if (!map.has(uid)) map.set(uid, entry);
    }
    return map;
  }, [accessLogs]);

  const formatDateTime = (d) => {
    try {
      return new Date(d).toLocaleString("de-DE");
    } catch (_) {
      return "";
    }
  };

  if (!isAgencyOwner) return <Navigate to="/agency" replace />;

  return (
    <div className="min-h-screen bg-muted">
      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-xl font-semibold text-foreground">{t("agency.pages.employeesTitle")}</h1>
          <button
            type="button"
            className="inline-flex items-center px-3 py-1.5 rounded bg-blue-600 text-white text-xs"
            onClick={() => setInviteOpen(true)}
          >
            {t("agency.employees.addButton")}
          </button>
        </div>

        {empLoading ? (
          <div className="text-sm text-muted-foreground">{t("common.loading")}</div>
        ) : employees.length === 0 ? (
          <div className="text-sm text-muted-foreground">{t("agency.employees.empty")}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {employees.map((e) => {
              const last = lastSwitchByAgencyUserId.get(e.id);
              const roleLabel =
                e.role === "OWNER" ? t("agency.employees.roleOwner") : t("agency.employees.roleEmployee");
              const isActive = e.active !== false;

              return (
                <div key={e.id} className="bg-background p-4 rounded shadow text-sm space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-foreground truncate">{e.email}</div>
                      <div className="text-xs text-muted-foreground">{t("agency.cards.role")}: {roleLabel}</div>
                      <div className="text-xs text-muted-foreground">{t("agency.employees.status")}: {isActive ? t("agency.employees.active") : t("agency.employees.inactive")}</div>
                    </div>
                    <div className="text-xs text-muted-foreground whitespace-nowrap">
                      {t("agency.cards.createdAt")}: {new Date(e.createdAt).toLocaleDateString("de-DE")}
                    </div>
                  </div>

                  <div className="text-xs text-muted-foreground">
                    {t("agency.cards.lastSwitch")}: {last ? `${last.company?.name || ""} · ${formatDateTime(last.createdAt)}` : t("agency.cards.lastSwitchNone")}
                  </div>

                  {e.role !== "OWNER" && (
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        className="text-xs underline"
                        onClick={async () => {
                          try {
                            const updated = await api.agencyToggleUserActive(token, e.id);
                            setEmployees((prev) => prev.map((x) => (x.id === e.id ? { ...x, ...updated } : x)));
                            toast({
                              title: updated.active ? t("agency.employees.activated") : t("agency.employees.deactivated"),
                            });
                          } catch (err) {
                            toast({
                              title: t("common.error"),
                              description: err.message,
                              variant: "destructive",
                            });
                          }
                        }}
                      >
                        {isActive ? t("agency.employees.deactivate") : t("agency.employees.activate")}
                      </button>

                      <button
                        type="button"
                        className="text-xs text-red-600 hover:underline"
                        onClick={async () => {
                          try {
                            await api.agencyDeleteUser(token, e.id);
                            setEmployees((prev) => prev.filter((x) => x.id !== e.id));
                          } catch (err) {
                            toast({
                              title: t("common.error"),
                              description: err.message,
                              variant: "destructive",
                            });
                          }
                        }}
                      >
                        {t("agency.employees.delete")}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {accessLogsLoading && (
          <div className="text-xs text-muted-foreground">{t("common.loading")}</div>
        )}

        <Dialog
          open={inviteOpen}
          onOpenChange={(v) => {
            if (!v && inviting) return;
            setInviteOpen(v);
            if (!v) {
              setNewEmpEmail("");
              setNewEmpRole("EMPLOYEE");
            }
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t("agency.employees.newTitle")}</DialogTitle>
            </DialogHeader>

            <div className="space-y-3">
              <div className="space-y-1">
                <label className="block text-[11px] text-muted-foreground">{t("agency.employees.email")}</label>
                <input
                  type="email"
                  className="border px-2 py-1 rounded text-xs w-full"
                  value={newEmpEmail}
                  onChange={(e) => setNewEmpEmail(e.target.value)}
                />
              </div>

              <div className="space-y-1">
                <label className="block text-[11px] text-muted-foreground">{t("agency.employees.role")}</label>
                <select
                  className="border px-2 py-1 rounded text-xs w-full"
                  value={newEmpRole}
                  onChange={(e) => setNewEmpRole(e.target.value)}
                >
                  <option value="EMPLOYEE">{t("agency.employees.roleEmployee")}</option>
                  <option value="OWNER">{t("agency.employees.roleOwner")}</option>
                </select>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <button
                type="button"
                className="inline-flex items-center justify-center px-3 py-1.5 rounded border text-xs"
                disabled={inviting}
                onClick={() => setInviteOpen(false)}
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center px-3 py-1.5 rounded bg-blue-600 text-white text-xs disabled:opacity-60"
                disabled={!newEmpEmail || inviting}
                onClick={async () => {
                  try {
                    setInviting(true);
                    await api.agencyInviteUser(token, { email: newEmpEmail, role: newEmpRole });
                    toast({ title: t("agency.employees.inviteCreated") });
                    setInviteOpen(false);
                  } catch (err) {
                    toast({
                      title: t("common.error"),
                      description: err.message,
                      variant: "destructive",
                    });
                  } finally {
                    setInviting(false);
                  }
                }}
              >
                {inviting ? t("agency.employees.inviting") : t("agency.employees.create")}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AgencyEmployeesPage;
